/**
 * MyRentalsPage — buyer's reservation history (active + past).
 *
 * Mounted at `/my-rentals`. Buyers reach it from the global nav after
 * completing a reservation on /rentals.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fmtMoney } from "../lib/money";
import { Calendar, Package, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

const STATUS_LABEL = {
  confirmed:  { label: "Confirmed", color: "#00F0FF" },
  active:     { label: "Active",    color: "#D4FF00" },
  returned:   { label: "Returned",  color: "#A1A1AA" },
  cancelled:  { label: "Cancelled", color: "#FF007F" },
};

export default function MyRentalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rentals/my-reservations");
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 pb-24" data-testid="my-rentals-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase">My rentals</h1>
            <p className="text-xs text-[#A1A1AA] mt-1">{items.length} reservation{items.length !== 1 ? "s" : ""}</p>
          </div>
          <Link to="/rentals" className="btn-primary text-xs" data-testid="my-rentals-browse-btn">
            <Calendar size={12} className="inline mr-1"/>Browse rentals
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#A1A1AA] text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="card p-10 text-center">
            <Package size={32} className="mx-auto text-[#A1A1AA] opacity-40 mb-2"/>
            <div className="font-display font-bold text-xl uppercase mb-1">No rentals yet</div>
            <div className="text-sm text-[#A1A1AA] mb-4">Browse nearby ateliers and reserve your first piece.</div>
            <Link to="/rentals" className="btn-primary text-xs">
              Explore the rental marketplace
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(r => {
              const meta = STATUS_LABEL[r.status] || STATUS_LABEL.confirmed;
              return (
                <div key={r.reservation_id} className="card p-4"
                  data-testid={`my-rental-${r.reservation_id}`}>
                  <div className="flex flex-wrap items-start gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">#{r.reservation_id.slice(-8).toUpperCase()}</div>
                      <div className="text-[10px] text-[#A1A1AA] flex items-center gap-2 mt-1">
                        <Calendar size={10}/>{r.start_date} → {r.end_date}
                        <span>· {r.days} days</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-black text-[#D4FF00] text-lg">{fmtMoney(r.grand_total)}</div>
                      <div className="text-[9px] text-[#A1A1AA] uppercase tracking-widest">
                        {fmtMoney(r.rental_total)} rent + {fmtMoney(r.deposit_total)} dep
                      </div>
                    </div>
                    <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded"
                      style={{ color: meta.color, background: `${meta.color}1A` }}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(r.items || []).map(it => (
                      <div key={it.product_id} className="flex items-center gap-2 bg-[#3D2845]/40 rounded-full pl-1 pr-3 py-0.5">
                        <img src={it.image} className="w-6 h-6 rounded-full object-cover" alt=""/>
                        <span className="text-[10px] font-bold truncate max-w-[160px]">{it.name}</span>
                      </div>
                    ))}
                  </div>
                  {r.status === "returned" && (
                    <div className="text-[10px] text-[#A1A1AA] flex items-center gap-1">
                      <CheckCircle2 size={10} className="text-[#A1A1AA]"/>
                      Returned · {fmtMoney(r.deposit_refund)} refunded
                      {r.damage_charge > 0 && <span> · {fmtMoney(r.damage_charge)} damage charged</span>}
                    </div>
                  )}
                  {r.status === "confirmed" && (
                    <div className="text-[10px] text-[#00F0FF] flex items-center gap-1">
                      <AlertCircle size={10}/>Pickup on <span className="font-bold">{r.start_date}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
